import { defineStore } from 'pinia';
import _ from 'lodash';
import fiveMixin from '@/composables/fiveMixin';
import { INITIAL_PARAM_DATA } from '@/pages/analysis/constants';
import { findTermsAPI } from '@/api/fortuneApi';
import { useAnalysisStore } from '@/store/modules/analysis';

export const useAnalysisDataStore = defineStore('analysisDataStore', () => {
  const { ...five } = fiveMixin();
  const reverseData = (data) => Object.values(data).reverse();

  const paramData = ref(_.cloneDeep(INITIAL_PARAM_DATA));
  const setParamData = (newData) => (paramData.value = newData);

  const sajuData = ref({});
  const setSajuData = (newData) => {
    sajuData.value = newData;

    transdata.value = newData.transdata;
    birthData.value = newData.birthganji;
    birthJijanggan.value = newData.jijanggan;
    daeunsData.value = {
      ...newData.daeuns,
      sky: reverseData(newData.daeuns.sky),
      earth: reverseData(newData.daeuns.earth),
    };
    daeunCurNum.value = newData.transdata.curlucknum;

    // 특수 파생값 처리
    const { sky, earth } = newData.birthganji;
    const skyValues = [
      five.getHanjaTxt(sky.year.value, 'sky'),
      five.getHanjaTxt(sky.month.value, 'sky'),
      five.getHanjaTxt(sky.day.value, 'sky'),
      five.getHanjaTxt(sky.hour.value, 'sky') || '-',
    ];
    const earthValues = [
      five.getHanjaTxt(earth.year.value, 'earth'),
      five.getHanjaTxt(earth.month.value, 'earth'),
      five.getHanjaTxt(earth.day.value, 'earth'),
      five.getHanjaTxt(earth.hour.value, 'earth') || '-',
    ];
    birthEightTxt.value = skyValues.join('') + earthValues.join('');

    const paljaSkyValues = [sky.hour.value, sky.day.value, sky.month.value, sky.year.value];
    const paljaEarthValues = [
      earth.hour.value,
      earth.day.value,
      earth.month.value,
      earth.year.value,
    ];
    baseCode.value = paljaSkyValues.join('') + paljaEarthValues.join('');
  };

  const inputdata = ref({});
  const setInputData = (newData) => (inputdata.value = newData);

  const transdata = ref({});
  const setTransData = (newData) => {
    transdata.value = newData;
  };

  const birthData = ref({});
  const setBirthData = (newData) => {
    birthData.value = newData;
  };

  const daeunsData = ref({});
  const setDaeunsData = (newData) => {
    daeunsData.value = newData;
  };

  const birthJijanggan = ref({});
  const setBirthJijanggan = (newData) => {
    birthJijanggan.value = newData;
  };

  const curDaeun = ref({});
  const setCurDaeun = (newData) => {
    curDaeun.value = newData;
  };

  const daeunCurNum = ref(0);
  const setDaeunCurNum = (newData) => {
    daeunCurNum.value = newData;
  };

  const birthEightTxt = ref('');
  const setBirthEightTxt = (newData) => {
    birthEightTxt.value = newData;
  };

  const sajuIlgan = computed(() => {
    if (!birthEightTxt.value) return '';
    const chars = birthEightTxt.value.split('');
    return chars[2] || '';
  });

  const baseCode = ref('');
  const setBaseCode = (newData) => {
    baseCode.value = newData;
  };

  // =============== 사주풀이 데이터 ===============
  const sData = ref({});
  const setSData = (newData) => {
    sData.value = newData;
  };

  const sajuKeywords = ref(null);
  const setSajuKeywords = (newData) => {
    sajuKeywords.value = newData;
  };

  // =============== 성향분석 데이터 ===============
  const pData = ref({});
  const setPData = (newData) => {
    pData.value = newData;
  };

  const pSubData = ref({});
  const setPSubData = (newData) => {
    pSubData.value = newData;
  };

  // =============== 낭만궁합 데이터 ===============
  const harmonyData = ref({});
  const setHarmonyData = (newData) => {
    harmonyData.value = newData;
  };

  // =============== 용신분석 데이터 ===============
  const yData = ref({});
  const setYData = (newData) => {
    yData.value = newData;
  };

  // =============== 신년운세 데이터 ===============
  const yearlyFortuneData = ref({});
  const setYearlyFortuneData = (newData) => {
    yearlyFortuneData.value = newData;
  };

  const yearlyFortuneSubData = ref({});
  const setYearlyFortuneSubData = (newData) => {
    yearlyFortuneSubData.value = newData;
  };

  /** 신년운세 경고 메시지 (대운범위초과 등) */
  const yearlyFortuneWarning = ref('');
  const setYearlyFortuneWarning = (message) => {
    yearlyFortuneWarning.value = message;
  };

  const resetAll = () => {
    paramData.value = _.cloneDeep(INITIAL_PARAM_DATA);

    sajuData.value = {};
    inputdata.value = {};
    transdata.value = {};
    birthData.value = {};
    daeunsData.value = {};
    birthJijanggan.value = {};
    curDaeun.value = {};
    daeunCurNum.value = 0;
    sData.value = {};
    sajuKeywords.value = null;
    dictionaryData.value = null;
    birthEightTxt.value = '';
    baseCode.value = '';

    pData.value = {};
    pSubData.value = {};

    harmonyData.value = {};

    yData.value = {};

    yearlyFortuneData.value = {};
    yearlyFortuneSubData.value = {};
    yearlyFortuneWarning.value = '';
  };

  // =============== 용어사전 데이터 ===============
  const dictionaryData = ref(null);
  const setDictionaryData = (newData) => {
    dictionaryData.value = newData;
  };
  const dictionaryItems = ref(null);
  const fetchSajuWords = async (words) => {
    if (words?.length > 0) {
      const params = {
        keywordCategory: '용어사전',
        keyword: words,
      };
      const result = await findTermsAPI(params);
      if (result) {
        dictionaryData.value = result.data;
        dictionaryItems.value = words.split(',');
      }
    }
  };

  watch([pData, sData, yData, yearlyFortuneData], ([newP, newS, newY, newYF]) => {
    const menu = useAnalysisStore().selectedMenu || '';
    let words = '';

    if (menu.startsWith('/yearly-fortune')) {
      // 신년운세: 8_행운용어 (배열 → 쉼표 문자열)
      const terms = newYF?.['8_행운용어'];
      words = Array.isArray(terms) ? terms.join(',') : '';
    } else if (menu.startsWith('/youngsin')) {
      // 용신분석
      words = newY?.용어해석 || '';
    } else if (menu.startsWith('/monthly-fortune')) {
      // 낭만월운: TODO - 데이터 소스 확정 후 적용
      words = newP?.용어해석 || '';
    } else {
      // 성향분석 / 사주풀이 등
      words = newP?.용어해석 || newS?.['0_용어해석'] || '';
    }

    if (words) {
      fetchSajuWords(words);
    }
  });

  return {
    paramData,
    setParamData,

    sajuData,
    setSajuData,

    inputdata,
    setInputData,

    transdata,
    setTransData,

    birthData,
    setBirthData,

    daeunsData,
    setDaeunsData,

    birthJijanggan,
    setBirthJijanggan,

    curDaeun,
    setCurDaeun,

    daeunCurNum,
    setDaeunCurNum,

    sData,
    setSData,

    sajuKeywords,
    setSajuKeywords,

    dictionaryData,
    setDictionaryData,

    birthEightTxt,
    setBirthEightTxt,

    sajuIlgan,

    baseCode,
    setBaseCode,

    resetAll,

    // =============== 성향분석 데이터 ===============
    pData,
    setPData,

    pSubData,
    setPSubData,

    dictionaryItems,

    harmonyData,
    setHarmonyData,

    yData,
    setYData,

    // =============== 신년운세 데이터 ===============
    yearlyFortuneData,
    setYearlyFortuneData,

    yearlyFortuneSubData,
    setYearlyFortuneSubData,

    yearlyFortuneWarning,
    setYearlyFortuneWarning,

    fetchSajuWords,
  };
});
